import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  GoogleImageService,
  ImageCategory,
  ImageItem
} from '../../gen2ai/google-image.service';

@Component({
  selector: 'app-image-library',
  templateUrl: './image-library.component.html',
  styleUrls: ['./image-library.component.scss']
})
export class ImageLibraryComponent implements OnInit, OnDestroy {
  searchForm: FormGroup;
  imageForm: FormGroup;

  images: ImageItem[] = [];
  pinnedImages: ImageItem[] = [];
  otherImages: ImageItem[] = [];
  labels: string[] = [];
  selectedLabel: string | null = null;

  primaryCategories: ImageCategory[] = [
    'Screenshots',
    'Diagrams',
    'UX/UI',
    'Marketing',
    'Misc'
  ];

  colorPalette: string[] = ['#0f172a', '#1f2937', '#334155', '#4b5563', '#020617'];
  defaultCardColor = '#020617';
  uploadCardColor = '#020617';

  selectedFile: File | null = null;
  selectedFileExt = '';
  selectedFileSizeKb = 0;

  isSubmitting = false;

  showArchived = false;

  private sub = new Subscription();

  constructor(
    private fb: FormBuilder,
    private imageService: GoogleImageService
  ) {
    this.searchForm = this.fb.group({
      query: ['']
    });

    this.imageForm = this.fb.group({
      title: [''],
      description: [''],
      primaryCategory: ['Screenshots'],
      extraLabels: [''],
      pinned: [false],
      color: [this.uploadCardColor]
    });
  }

  ngOnInit(): void {
    this.sub.add(
      this.imageService.images$.subscribe((imgs) => {
        this.images = imgs;
        this.refreshLabels();
        this.refreshView();
      })
    );

    this.sub.add(
      this.searchForm.get('query')!.valueChanges.subscribe(() => {
        this.refreshView();
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /* ------------------------------------------------------------------ */
  /*  Upload handling                                                   */
  /* ------------------------------------------------------------------ */

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) {
      this.selectedFile = null;
      this.selectedFileExt = '';
      this.selectedFileSizeKb = 0;
      return;
    }

    const file = input.files[0];
    this.selectedFile = file;

    const ext = (file.name.split('.').pop() || '').toUpperCase();
    this.selectedFileExt = ext || 'IMG';
    this.selectedFileSizeKb = Math.round(file.size / 1024);
  }

  async createImage(): Promise<void> {
    if (!this.selectedFile || this.imageForm.invalid || this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;
    try {
      const formValue = this.imageForm.value;

      const extraLabels = (formValue.extraLabels || '')
        .split(',')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);

      await this.imageService.createImage(
        {
          title: formValue.title || this.selectedFile.name,
          description: formValue.description || '',
          primaryCategory: formValue.primaryCategory,
          extraLabels,
          color: formValue.color,
          pinned: formValue.pinned
        },
        this.selectedFile
      );

      this.imageForm.reset({
        title: '',
        description: '',
        primaryCategory: 'Screenshots',
        extraLabels: '',
        pinned: false,
        color: this.uploadCardColor
      });
      this.selectedFile = null;
      this.selectedFileExt = '';
      this.selectedFileSizeKb = 0;
    } catch (err: any) {
      console.error('Failed to upload image', err);
      alert(err?.message || 'Failed to upload image.');
    } finally {
      this.isSubmitting = false;
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Filters + view model                                              */
  /* ------------------------------------------------------------------ */

  toggleArchivedView(): void {
    this.showArchived = !this.showArchived;
    this.refreshView();
  }

  selectLabel(label: string | null): void {
    this.selectedLabel = label;
    this.refreshView();
  }

  private refreshLabels(): void {
    const labelSet = new Set<string>();
    this.images.forEach((img) => {
      (img.labels || []).forEach((lbl) => labelSet.add(lbl));
    });
    this.labels = Array.from(labelSet).sort();
  }

  private refreshView(): void {
    const q = (this.searchForm.get('query')!.value || '').toLowerCase().trim();

    let filtered = this.images.filter((img) =>
      this.showArchived ? img.archived : !img.archived
    );

    if (this.selectedLabel) {
      filtered = filtered.filter((img) =>
        img.labels?.includes(this.selectedLabel as string)
      );
    }

    if (q) {
      filtered = filtered.filter((img) => {
        return (
          img.title.toLowerCase().includes(q) ||
          img.description.toLowerCase().includes(q) ||
          img.primaryCategory.toLowerCase().includes(q) ||
          (img.labels || []).some((l) => l.toLowerCase().includes(q))
        );
      });
    }

    filtered.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    this.pinnedImages = filtered.filter((i) => i.pinned);
    this.otherImages = filtered.filter((i) => !i.pinned);
  }

  /* ------------------------------------------------------------------ */
  /*  Actions                                                           */
  /* ------------------------------------------------------------------ */

  async togglePinned(item: ImageItem, pinned: boolean): Promise<void> {
    await this.imageService.togglePinned(item.id, pinned);
  }

  async toggleArchived(item: ImageItem): Promise<void> {
    await this.imageService.toggleArchived(item.id, !item.archived);
  }

  async delete(item: ImageItem): Promise<void> {
    if (!confirm('Delete this image from your library?')) return;
    await this.imageService.deleteImage(item.id);
  }

  openImage(item: ImageItem): void {
    if (!item.imageUrl) return;
    window.open(item.imageUrl, '_blank', 'noopener');
  }

  useInClarichat(item: ImageItem): void {
    this.imageService.setClarichatImage(item);
  }

  changeColor(item: ImageItem, color: string): void {
    this.imageService.updateImage(item.id, { color });
  }
}
